// src/screens/Calcleite/CalcleiteMain.js

import React, { useState, useMemo, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { signOut } from "firebase/auth";
import { auth } from "../../firebaseconfig";
import { useAuth } from "../../hooks/useAuth";
import "./CalcleiteMain.css";
import { FaSignOutAlt, FaTimes, FaChevronRight } from "react-icons/fa";

// Importe os ícones
import logo from "../../assets/calcleite.png";
import manualIcon from "../../assets/manual.png";
import producaoIcon from "../../assets/producao.png";
import capitalIcon from "../../assets/capital.png";
import custosIcon from "../../assets/custos.png";
import receitaIcon from "../../assets/receita.png";
import relatorioIcon from "../../assets/relatorio.png";

const toDate = (timestamp) => {
  if (!timestamp) return null;
  if (typeof timestamp.toDate === "function") {
    return timestamp.toDate();
  }
  const date = new Date(timestamp);
  return isNaN(date) ? null : date;
};

const CalcleiteMain = () => {
  const { user, userData, loading } = useAuth();
  const navigate = useNavigate();

  const [modalVisible, setModalVisible] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);

  const APP_TAG = "calcleite";
  const lastSyncAttemptRef = useRef(0);

  const menuItems = [
    {
      title: "Instruções",
      desc: "Guia passo a passo",
      icon: manualIcon,
      path: "/calcleite/app/instrucoes",
    },
    {
      title: "Produção",
      desc: "Indicadores zootécnicos",
      icon: producaoIcon,
      path: "/calcleite/app/producao",
    },
    {
      title: "Capital Investido",
      desc: "Depreciação e ativos",
      icon: capitalIcon,
      path: "/calcleite/app/capital",
    },
    {
      title: "Custo Operacional",
      desc: "Controle de despesas",
      icon: custosIcon,
      path: "/calcleite/app/custos",
    },
    {
      title: "Receita",
      desc: "Análise de rentabilidade",
      icon: receitaIcon,
      path: "/calcleite/app/receita",
    },
    {
      title: "Relatório Final",
      desc: "Exportar PDF completo",
      icon: relatorioIcon,
      path: "/calcleite/app/relatorio",
    },
  ];

  const syncSubscription = async () => {
    if (!user || isSyncing) return;
    const now = Date.now();
    if (now - lastSyncAttemptRef.current < 10000) return;
    setIsSyncing(true);
    lastSyncAttemptRef.current = now;
    try {
      const token = await user.getIdToken(true);
      await fetch(
        "https://backendcustosagro-e51a.onrender.com/sync-subscription",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ uid: user.uid }),
        },
      );
    } catch (error) {
      console.error("Erro sync:", error);
    } finally {
      setIsSyncing(false);
    }
  };

  useEffect(() => {
    if (user) syncSubscription();
  }, [user]);

  useEffect(() => {
    if (!loading && userData && !userData[APP_TAG]) {
      alert("Sua assinatura expirou.");
      navigate("/calcleite/planos");
    }
  }, [userData, loading, navigate]);

  const subscriptionData = useMemo(
    () => userData?.subscriptions?.[APP_TAG] || {},
    [userData],
  );
  const dataTermino = useMemo(
    () => toDate(subscriptionData.currentPeriodEnd),
    [subscriptionData],
  );
  const diasRestantes = useMemo(() => {
    if (!dataTermino) return null;
    const hoje = new Date();
    const diffTime = Math.max(0, dataTermino.getTime() - hoje.getTime());
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24)).toString();
  }, [dataTermino]);

  const handleLogout = async () => {
    await signOut(auth);
    navigate("/login");
  };

  const handleCancelSubscription = async () => {
    if (!window.confirm("Deseja cancelar a renovação automática?")) return;
    setCancelLoading(true);
    try {
      const token = await user.getIdToken(true);
      const res = await fetch(
        "https://backendcustosagro-e51a.onrender.com/cancel-subscription",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ uid: user.uid, app: APP_TAG }),
        },
      );
      const json = await res.json();
      if (!json.success) throw new Error(json.error);
      await syncSubscription();
      alert("Renovação cancelada.");
      setModalVisible(false);
    } catch (e) {
      alert(e.message);
    } finally {
      setCancelLoading(false);
    }
  };

  if (loading) return <div className="clm-loader">Carregando painel...</div>;

  const willCancel =
    userData?.[
      `willCancelAtPeriodEnd${APP_TAG.charAt(0).toUpperCase() + APP_TAG.slice(1)}`
    ];
  const firstName = userData?.nomeCompleto?.split(" ")[0] || "Produtor";

  return (
    <div className="clm-dashboard">
      {/* 1. Hero Banner (Apenas decorativo e de boas-vindas) */}
      <header className="clm-hero-banner">
        <div className="clm-hero-overlay"></div>

        <div className="clm-hero-top-row">
          <div className="clm-brand-box">
            <img src={logo} alt="CalcLeite Logo" />
            <span>CalcLeite</span>
          </div>
          {/* Engrenagem removida daqui, pois já existe no Header global */}
        </div>

        <div className="clm-hero-welcome">
          <h1>Olá, {firstName}!</h1>
          <p>Bem-vindo ao seu painel de gestão leiteira.</p>
        </div>
      </header>

      {/* 2. Conteúdo Principal (Grid de Ferramentas - Agora na área branca) */}
      <div className="clm-content-wrapper">
        <section className="clm-section-tools">
          <div className="clm-tools-grid">
            {menuItems.map((item, index) => (
              <div
                key={index}
                className="clm-tool-card"
                onClick={() => navigate(item.path)}
              >
                <div className="clm-tool-icon">
                  <img src={item.icon} alt={item.title} />
                </div>
                <div className="clm-tool-info">
                  <h3>{item.title}</h3>
                  <p>{item.desc}</p>
                </div>
                <FaChevronRight className="clm-card-arrow" />
              </div>
            ))}
          </div>
        </section>
      </div>

      {/* Modal de Configurações (Acionado pelo Header Global se necessário, ou lógica futura) */}
      {/* MANTIDO CASO QUEIRA REATIVAR O BOTÃO DEPOIS, OU INTEGRAR AO HEADER GLOBAL */}
      {modalVisible && (
        <div className="clm-overlay" onClick={() => setModalVisible(false)}>
          <div className="clm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="clm-modal-head">
              <h3>Minha Assinatura</h3>
              <button onClick={() => setModalVisible(false)}>
                <FaTimes />
              </button>
            </div>
            <div className="clm-modal-body">
              <div className="clm-sub-info">
                <p>
                  <strong>Status:</strong>{" "}
                  <span className={subscriptionData.status}>
                    {subscriptionData.status}
                  </span>
                </p>
                <p>
                  <strong>Plano:</strong> {subscriptionData.plan}
                </p>
                {diasRestantes && (
                  <p>
                    <strong>Expira em:</strong> {diasRestantes} dias
                  </p>
                )}
              </div>

              <div className="clm-modal-footer">
                {!willCancel && subscriptionData.status === "active" && (
                  <button
                    className="clm-btn-cancel"
                    onClick={handleCancelSubscription}
                    disabled={cancelLoading}
                  >
                    {cancelLoading ? "Processando..." : "Cancelar Renovação"}
                  </button>
                )}
                <button className="clm-btn-logout" onClick={handleLogout}>
                  <FaSignOutAlt /> Sair da Conta
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CalcleiteMain;
